import { useCallback, useEffect, useState } from 'react'
import { api } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Dato, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Riquadro, Scelta, Tabella } from '../componenti/base.tsx'
import { messaggioErrore } from '../componenti/elenco.tsx'
import { Segnalazioni, type Regola } from './migrazione/Segnalazioni.tsx'
import { DecisioniDaPrendere } from './migrazione/DecisioniDaPrendere.tsx'
import { inOrdineDiSede, nomeSede } from '../servizi/sedi.ts'

type StatoMigrazione = {
  esecuzioni: { id: string; avviata_il: string; conclusa_il: string | null; ambiente: string
    righe_lette: number | null; righe_scritte: number | null; anomalie: number | null; esito: string | null }[]
  per_entita: { entita: string; esito: string; quante: number }[]
  anomalie: { regola: string; gravita: string; stato: string; quante: number }[]
  decisioni: { tipo: string; stato: string; quante: number }[]
  comuni: { confermati: number; da_confermare: number; senza_corrispondenza: number }
  sedi_da_operatore: { senza_sede: number; assegnabili: number; gia_assegnate: number }
  /** Tipi di segnalazione presenti, con la spiegazione del catalogo (461) e i conteggi per stato. */
  regole: Regola[]
}

/**
 * Pagina Migrazione.
 *
 * Dal 19/10/2026 (461) e' una pagina di lavoro, aperta anche alla Direzione: in testa le
 * segnalazioni da sistemare e le decisioni da prendere, una per una, con che cosa significano e che
 * cosa fare; in coda l'andamento del travaso, che serve a chi lo esegue.
 */
export function Migrazione() {
  const { puo } = useSessione()
  const [stato, setStato] = useState<StatoMigrazione | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [assegnazione, setAssegnazione] = useState<{ inCorso: boolean; esito: number | null }>(
    { inCorso: false, esito: null })

  const caricaStato = useCallback(() => {
    api.get<StatoMigrazione>('/migrazione/stato')
      .then(setStato)
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [])
  useEffect(caricaStato, [caricaStato])

  async function assegnaSediDaOperatore() {
    setAssegnazione({ inCorso: true, esito: null })
    setErrore(null)
    try {
      const r = await api.post<{ assegnate: number }>('/migrazione/sedi-da-operatore')
      setAssegnazione({ inCorso: false, esito: r.assegnate })
      caricaStato()
    } catch (e) {
      setErrore(messaggioErrore(e))
      setAssegnazione({ inCorso: false, esito: null })
    }
  }

  if (errore) return <Avviso tipo="errore">{errore}</Avviso>
  if (!stato) return <Attesa testo="Caricamento…" />

  return (
    <>
      <Intestazione titolo="Migrazione"
        sottotitolo="Dati portati dal vecchio sistema: segnalazioni da sistemare e decisioni da prendere." />

      <Segnalazioni regole={stato.regole} ricaricaStato={caricaStato} />

      <DecisioniDaPrendere />

      <h2 className="migrazione-sezione">Andamento del travaso</h2>

      <Riquadro titolo="Ultime dieci esecuzioni" denso>
        <Tabella intestazioni={['Avviata', 'Ambiente', 'Lette', 'Scritte', 'Anomalie', 'Esito']}
                 vuoto={<Vuoto testo="La migrazione non è ancora stata eseguita." />}>
          {stato.esecuzioni.map((e) => (
            <tr key={e.id}>
              <td data-etichetta="Avviata">{new Date(e.avviata_il).toLocaleString('it-IT')}</td>
              <td data-etichetta="Ambiente">{e.ambiente}</td>
              <td data-etichetta="Lette" className="mono">{e.righe_lette ?? '—'}</td>
              <td data-etichetta="Scritte" className="mono">{e.righe_scritte ?? '—'}</td>
              <td data-etichetta="Anomalie" className="mono">{e.anomalie ?? '—'}</td>
              <td data-etichetta="Esito">
                <Etichetta tono={e.esito === 'ok' ? 'positivo' : e.conclusa_il ? 'attenzione' : 'neutro'}>
                  {e.esito ?? 'in corso'}
                </Etichetta>
              </td>
            </tr>
          ))}
        </Tabella>
      </Riquadro>

      <div className="griglia griglia-riquadri">
        <Riquadro titolo="Righe migrate" denso>
          <Tabella intestazioni={['Entità', 'Esito', 'Numero']}
                   vuoto={<Vuoto testo="Nessuna riga migrata." />}>
            {stato.per_entita.map((r, i) => (
              <tr key={i}>
                <td data-etichetta="Entità">{r.entita}</td>
                <td data-etichetta="Esito">
                  <Etichetta tono={r.esito === 'migrato' ? 'positivo' : 'attenzione'}>{r.esito}</Etichetta>
                </td>
                <td data-etichetta="Numero" className="mono a-destra">{r.quante}</td>
              </tr>
            ))}
          </Tabella>
        </Riquadro>

        {/*
          * Sede dei servizi dalla sede principale dell'operatore (decisione del 16/09/2026).
          *
          * La funzione esiste dalla migrazione 376 e la migrazione la esegue una volta sola,
          * quando viene applicata. La sua utilita' pero' arriva dopo, perche' le utenze
          * ricevono la sede principale man mano che l'anagrafica degli operatori si completa:
          * senza un posto da cui rieseguirla, le righe del registro restavano senza sede — e la
          * sede e' il criterio con cui ogni incarico di sede guarda il registro.
          */}
        <Riquadro titolo="Sede dei servizi dall’operatore">
          <Dato nome="Righe senza sede">
            <span className="mono">{stato.sedi_da_operatore.senza_sede}</span>
          </Dato>
          <Dato nome="Assegnabili oggi">
            <span className="mono">{stato.sedi_da_operatore.assegnabili}</span>
          </Dato>
          <Dato nome="Già assegnate">
            <span className="mono">{stato.sedi_da_operatore.gia_assegnate}</span>
          </Dato>
          <p className="tenue piccolo">
            Alle righe senza sede si assegna la sede principale dell’operatore che le ha
            registrate, con l’anomalia <span className="mono">SEDE_DA_OPERATORE</span> a dirlo.
            Si può rieseguire: tocca solo le righe che la sede non ce l’hanno, e va rieseguita
            man mano che le utenze ricevono la sede principale.
          </p>
          {assegnazione.esito !== null && (
            <Avviso tipo="successo">
              {assegnazione.esito === 0
                ? 'Nessuna riga da assegnare: o la sede c’è già, o l’operatore non ha una sede principale.'
                : `${assegnazione.esito} righe hanno preso la sede dell’operatore.`}
            </Avviso>
          )}
          {puo('migrazione.decidi') && (
            <Bottone variante="primario" disabled={assegnazione.inCorso}
                     onClick={() => void assegnaSediDaOperatore()}>
              {assegnazione.inCorso ? 'Assegnazione in corso…' : 'Assegna le sedi dall’operatore'}
            </Bottone>
          )}
        </Riquadro>

        <Riquadro titolo="Corrispondenza dei comuni">
          <Dato nome="Confermati"><span className="mono">{stato.comuni.confermati}</span></Dato>
          <Dato nome="Da verificare"><span className="mono">{stato.comuni.da_confermare}</span></Dato>
          <Dato nome="Senza corrispondenza">
            <span className="mono">{stato.comuni.senza_corrispondenza}</span>
          </Dato>
        </Riquadro>
      </div>

      <SediDaEnti />
    </>
  )
}

// --- sedi CIDIS registrate come enti (N-2, CAT-15) ----------------------------------------------

type Riferimenti = Record<string, number | string>

type Proposta = {
  ente: string
  comune: string | null
  sede_proposta: string
  sede_id: string | null
  regola: string
  ambigua: boolean
  riferimenti: Riferimenti
  domanda: string
}

type DecisioneSede = {
  id: string
  chiave: string
  proposta: Proposta
  scelta: { sede_id?: string } | null
  stato: 'da_confermare' | 'confermata' | 'modificata' | 'rinviata'
  nota: string | null
  decisa_il: string | null
  stakeholder_id: string | null
  ente: string | null
  ente_attivo: boolean | null
  comune_nome: string | null
  provincia_sigla: string | null
}

type Applicata = {
  stakeholder_id: string
  nome_originale: string
  sede_id: string
  sede: string
  sede_codice: string
  regola: string
  sede_nuova: boolean
  eseguita_il: string
  riferimenti: number
}

type SedeScelta = {
  id: string; codice: string; nome: string; tipo: string; attiva: boolean
  sede_padre_id: string | null; sede_padre_nome: string | null
}

type SediDaEnti = { decisioni: DecisioneSede[]; applicate: Applicata[]; sedi: SedeScelta[] }

const TONO_STATO: Record<DecisioneSede['stato'], 'positivo' | 'attenzione' | 'neutro'> = {
  da_confermare: 'attenzione', confermata: 'positivo', modificata: 'positivo', rinviata: 'neutro',
}

/** Quanti riferimenti si sposterebbero: il numero che CIDIS guarda per capire il peso. */
const quantiRiferimenti = (r: Riferimenti) =>
  Object.values(r ?? {}).reduce<number>((s, v) => s + Number(v || 0), 0)

/**
 * Sezione «Sedi CIDIS registrate come enti» (N-2, CAT-15).
 *
 * Nel vecchio archivio le sedi di CIDIS erano registrate come enti esterni. Lo script
 * `sedi-da-enti` propone un abbinamento ente → sede e apre una decisione per ente; **la
 * trasformazione non parte finché resta un solo abbinamento da confermare**, e finora non
 * esisteva nessun posto da cui confermarlo: la rotta che serve questa sezione non aveva
 * consumatori, quindi il passo di migrazione era fermo per costruzione.
 *
 * La decisione del committente fissa il principio, non le singole corrispondenze: qui si
 * conferma la proposta o si sceglie un'altra sede. La correzione vale per l'intero gruppo di
 * doppioni — stesso nome senza forma giuridica, stesso comune — e due correzioni discordi nello
 * stesso gruppo fermano lo script con un messaggio esplicito.
 */
function SediDaEnti() {
  const { puo } = useSessione()
  const [dati, setDati] = useState<SediDaEnti | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const [correzioni, setCorrezioni] = useState<Record<string, string>>({})

  const carica = useCallback(() => {
    api.get<SediDaEnti>('/migrazione/sedi-da-enti')
      .then(setDati)
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [])
  useEffect(carica, [carica])

  const decide = async (d: DecisioneSede, corpo: Record<string, unknown>) => {
    setInCorso(d.id); setErrore(null)
    try {
      await api.post(`/migrazione/decisioni/${d.id}`, corpo)
      carica()
    } catch (e) { setErrore(messaggioErrore(e)) } finally { setInCorso(null) }
  }

  if (!dati) return <Riquadro titolo="Sedi CIDIS registrate come enti"><Attesa /></Riquadro>

  const daConfermare = dati.decisioni.filter((d) => d.stato === 'da_confermare').length
  // Ogni sede seguita dai suoi sportelli (463), come nelle altre tendine.
  const opzioniSedi = inOrdineDiSede(dati.sedi.filter((s) => s.attiva))
    .map((s) => ({ valore: s.id, etichetta: `${s.codice} — ${nomeSede(s)}` }))
  const puoDecidere = puo('migrazione.decidi')

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      <Riquadro titolo="Sedi CIDIS registrate come enti">
        <p className="tenue">
          Nel vecchio archivio le sedi di CIDIS erano registrate come enti esterni. Ogni riga è un
          abbinamento proposto fra un ente e la sede che prenderà il suo posto: la trasformazione
          non parte finché resta anche un solo abbinamento da confermare. Correggere significa
          scegliere un’altra sede; la correzione vale per tutto il gruppo di doppioni.
        </p>
        {daConfermare > 0 && (
          <Avviso tipo="attenzione">
            {daConfermare === 1
              ? 'Un abbinamento è ancora da confermare: finché resta, la trasformazione non parte.'
              : `${daConfermare} abbinamenti sono ancora da confermare: finché restano, la trasformazione non parte.`}
          </Avviso>
        )}
        <Tabella intestazioni={['Ente', 'Comune', 'Sede proposta', 'Regola', 'Riferimenti', 'Stato', '']}
                 vuoto={<Vuoto testo="Nessun abbinamento proposto: la prova a secco non è ancora stata eseguita." />}>
          {dati.decisioni.map((d) => {
            const sedeScelta = d.scelta?.sede_id
            const nomeScelta = dati.sedi.find((s) => s.id === sedeScelta)
            return (
              <tr key={d.id}>
                <td data-etichetta="Ente">
                  {d.ente ?? d.proposta.ente}
                  {d.ente_attivo === false && <> <Etichetta tono="neutro">già trasformato</Etichetta></>}
                </td>
                <td data-etichetta="Comune">
                  {d.comune_nome
                    ? `${d.comune_nome}${d.provincia_sigla ? ` (${d.provincia_sigla})` : ''}`
                    : d.proposta.comune ?? '—'}
                </td>
                <td data-etichetta="Sede proposta">
                  {nomeScelta ? `${nomeScelta.codice} — ${nomeScelta.nome}` : d.proposta.sede_proposta}
                  {d.proposta.ambigua && (
                    <p className="piccolo tenue">{d.proposta.domanda}</p>
                  )}
                </td>
                <td data-etichetta="Regola" className="piccolo">{d.proposta.regola}</td>
                <td data-etichetta="Riferimenti" className="mono a-destra"
                    title={Object.entries(d.proposta.riferimenti ?? {})
                      .map(([k, v]) => `${k}: ${v}`).join('\n')}>
                  {quantiRiferimenti(d.proposta.riferimenti)}
                </td>
                <td data-etichetta="Stato">
                  <Etichetta tono={TONO_STATO[d.stato]}>{d.stato.replace('_', ' ')}</Etichetta>
                </td>
                <td data-etichetta="">
                  {puoDecidere && (
                    <div className="riga">
                      <Scelta etichetta="Altra sede" valore={correzioni[d.id] ?? ''}
                              vuota="— sede proposta —" opzioni={opzioniSedi}
                              cambia={(v) => setCorrezioni((c) => ({ ...c, [d.id]: v }))} />
                      {correzioni[d.id]
                        ? (
                          <Bottone disabled={inCorso === d.id}
                                   onClick={() => void decide(d, {
                                     stato: 'modificata', scelta: { sede_id: correzioni[d.id] },
                                     nota: 'abbinamento corretto dalla pagina Migrazione',
                                   })}>
                            Correggi
                          </Bottone>
                        )
                        : (
                          <Bottone variante="primario" disabled={inCorso === d.id}
                                   onClick={() => void decide(d, { stato: 'confermata' })}>
                            Conferma
                          </Bottone>
                        )}
                      <Bottone variante="discreto" disabled={inCorso === d.id}
                               onClick={() => void decide(d, { stato: 'rinviata' })}>
                        Rinvia
                      </Bottone>
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </Tabella>
      </Riquadro>

      <Riquadro titolo="Abbinamenti già applicati" denso>
        <Tabella intestazioni={['Ente di origine', 'Sede', 'Regola', 'Riferimenti spostati', 'Quando']}
                 vuoto={<Vuoto testo="Nessun ente è ancora stato trasformato in sede." />}>
          {dati.applicate.map((a) => (
            <tr key={`${a.stakeholder_id} ${a.sede_id}`}>
              <td data-etichetta="Ente di origine">{a.nome_originale}</td>
              <td data-etichetta="Sede">
                {a.sede_codice} — {a.sede}
                {a.sede_nuova && <> <Etichetta tono="neutro">creata</Etichetta></>}
              </td>
              <td data-etichetta="Regola" className="piccolo">{a.regola}</td>
              <td data-etichetta="Riferimenti spostati" className="mono a-destra">{a.riferimenti}</td>
              <td data-etichetta="Quando">{new Date(a.eseguita_il).toLocaleString('it-IT')}</td>
            </tr>
          ))}
        </Tabella>
      </Riquadro>
    </>
  )
}

